import { Button } from "@chakra-ui/react/button";
import { Dialog } from "@chakra-ui/react/dialog";
import { useBreakpointValue, useDisclosure } from "@chakra-ui/react/hooks";

import { Field } from "components/ui/field";
import {
  PasswordInput,
  PasswordStrengthMeter,
} from "components/ui/password-input";
import PasswordChangeConfirmation from "components/user-settings/AccountOverview/PasswordChange";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import * as yup from "yup";

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getPasswordStrength = (password: string): number => {
  const strength = {
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    moreThan16: false,
    noMoreThan2Identical: true,
  };

  if (password.length >= 16) {
    strength.moreThan16 = true;
  }

  for (let i = 0; i < password.length - 2; i++) {
    if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
      strength.noMoreThan2Identical = false;
      break;
    }
  }

  for (const char of password) {
    if (char.match(/[a-z]/)) {
      strength.hasLowerCase = true;
    } else if (char.match(/[A-Z]/)) {
      strength.hasUpperCase = true;
    } else if (char.match(/[0-9]/)) {
      strength.hasNumber = true;
    } else if (char.match(/[!@#$%^&*]/)) {
      strength.hasSpecialChar = true;
    }
  }
  return Object.values(strength).filter((value) => value).length;
};

const PasswordChangeModal = ({ isOpen, onClose }: PasswordChangeModalProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const passwordChangeConfirmationDrawer = useDisclosure();

  const getValidationError = async (password: string): Promise<string> => {
    const schema = yup
      .string()
      .required("Password is required")
      .min(16, "Must be at least 16 characters")
      .matches(/^(?=.*[a-z])/, "Must contain one lowercase character")
      .matches(/^(?=.*[A-Z])/, "Must contain one uppercase character")
      .matches(/^(?=.*[0-9])/, "Must contain at least one numeric character")
      .matches(
        /^(?=.*[!@#$%^&*])/,
        "Must contain at least one special character (!@#$%^&*)"
      )
      .test(
        "no-triple-repeats",
        "Must not contain more than two identical characters in a row",
        (value) => {
          if (!value) return false;
          for (let i = 0; i < value.length - 2; i++) {
            if (value[i] === value[i + 1] && value[i] === value[i + 2]) {
              return false;
            }
          }
          return true;
        }
      );

    try {
      await schema.validate(password);
    } catch (err: unknown) {
      if (err instanceof yup.ValidationError) {
        if (err.errors[0]) {
          return err.errors[0];
        }
      }
    }
    return "";
  };

  useEffect(() => {
    setPasswordError("");
    setNewPassword("");
  }, [isOpen]);

  useEffect(() => {
    if (!newPassword) {
      setPasswordError("");
    } else {
      getValidationError(newPassword).then((error) => {
        setPasswordError(error);
      });
    }
  }, [newPassword]);

  return (
    <>
      <Dialog.Root
        open={isOpen}
        trapFocus={false}
        onOpenChange={({ open: newIsOpen }) => {
          if (!newIsOpen) {
            onClose();
          }
        }}
        size={isBaseSize ? "full" : "md"}
      >
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Change Password</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Field
                label="New Password"
                invalid={!!passwordError}
                errorText={passwordError}
              >
                <PasswordInput
                  autoFocus={true}
                  data-autofocus
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={passwordChangeConfirmationDrawer.open}
                />
                <PasswordStrengthMeter
                  value={getPasswordStrength(newPassword)}
                  max={5}
                />
              </Field>
            </Dialog.Body>
            <Dialog.Footer justifyContent="space-between">
              <Button
                colorPalette="gray"
                variant="ghost"
                onClick={onClose}
                disabled={passwordChangeConfirmationDrawer.open}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  !!passwordError ||
                  !newPassword ||
                  passwordChangeConfirmationDrawer.open
                }
                colorPalette={colorScheme}
                onClick={() => passwordChangeConfirmationDrawer.onOpen()}
              >
                Change
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
          {/* It should always be inside another positioner */}
          <PasswordChangeConfirmation
            newPassword={newPassword}
            isOpen={passwordChangeConfirmationDrawer.open}
            onClose={() => {
              passwordChangeConfirmationDrawer.onClose();
              onClose();
            }}
          />
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );
};

export default PasswordChangeModal;
